import { UserApi } from "@/services/users-service"
import { useAuth } from "@/stores/auth"
import { useSystemFeedback } from "@/stores/systemFeedback"
import { defineStore } from "pinia"
import type { User } from "@/types/user"
import type { Pagination } from "@/types/pagination"
import type { ApiError } from "@/types/api"

export const useUsers = defineStore("users", {
  state: () => {
    return {
      listUsers: {} as Pagination<User>,
      user: {} as User,
      api: new UserApi()
    }
  },

  actions: {
    async createUser(data: FormData) {
      try {
        const response = await this.api.createUser(data)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async updateUser(data: FormData, username: string) {
      try {
        const response = await this.api.updateUser(data, username)
        await this.updateLoggedUser(username)
        this.handleSuccessMessage(response.data.detail)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async deleteUserImage(username: string) {
      try {
        const response = await this.api.deleteUserImage(username)
        this.user.image_url = ""
        await this.updateLoggedUser(username)
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async getUser(username: string) {
      try {
        const response = await this.api.getUser(username)
        this.user = response.data
        return response
      } catch (error) {
        this.user = {} as User
        this.handleErrors(error as ApiError)
      }
    },

    async getUsers(
      page: number,
      search?: string,
      order?: string,
      perPage?: number
    ) {
      try {
        const response = await this.api.getAllUsers(
          page,
          search,
          order,
          perPage
        )
        this.listUsers = response.data
        return response
      } catch (error) {
        this.listUsers = {} as Pagination<User>
        this.handleErrors(error as ApiError)
      }
    },

    async getUserByToken() {
      return await this.api.getUserByToken()
    },

    handleSuccessMessage(message: string) {
      useSystemFeedback().setFeedback({
        type: "success",
        text: `backend.${message}`
      })
    },

    async updateLoggedUser(username: string) {
      const authStore = useAuth()
      if (username === authStore.loggedUser.username) {
        await authStore.getLoggedUser()
      }
    },

    handleErrors(error: ApiError) {
      const errorMessage = error?.response
        ? `backend.${error.response.data.detail}`
        : "requestError.default"
      useSystemFeedback().setFeedback({ type: "danger", text: errorMessage })
    }
  }
})
